'use client'

/**
 * InlineFreeAlertsCta — a compact "get free alerts" conversion bar for proof surfaces (e.g. under the
 * Caught Early receipts). Renders ONLY for logged-out visitors (the conversion target); signed-in users
 * and the loading state see nothing (no flash). One-click Google is primary; email signup is the
 * fallback. Reuses the same free-account + 5-premium-days offer as StockSignupCta.
 */

import { useSession, signIn } from 'next-auth/react'
import Link from 'next/link'
import { ArrowRight, Bell } from 'lucide-react'

export default function InlineFreeAlertsCta({ headline }: { headline?: string }) {
  const { status } = useSession()
  if (status !== 'unauthenticated') return null
  // New signups land on /welcome (the guided activation flow) — the biggest lever on trial→paid.
  const callbackUrl = '/welcome'

  return (
    <div className="mt-3 flex flex-col gap-2.5 rounded-xl border border-emerald-300 bg-emerald-50 dark:border-emerald-500/25 dark:bg-emerald-500/[0.06] px-4 py-3 sm:flex-row sm:items-center sm:gap-3">
      <p className="min-w-0 flex-1 text-sm font-semibold leading-snug text-foreground">
        <Bell className="mr-1.5 -mt-0.5 inline h-3.5 w-3.5 text-emerald-700 dark:text-emerald-400" />
        {headline ?? 'Want the next one the moment it fires? Get free real-time unusual-volume alerts.'}
      </p>
      <div className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={() => signIn('google', { callbackUrl })}
          className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 text-[13px] font-bold text-zinc-900 shadow-sm transition-colors hover:bg-zinc-100"
        >
          <GoogleG /> Continue with Google
        </button>
        <Link prefetch={false}
          href="/auth?mode=signup&callbackUrl=/welcome"
          className="flex items-center gap-1.5 rounded-lg border border-border bg-muted px-3 py-2 text-[13px] font-semibold text-foreground/80 transition-colors hover:bg-accent dark:hover:bg-white/10"
        >
          Sign up free <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
      <span className="text-center text-[11px] text-muted-foreground sm:ml-1 sm:text-left">No card · 5 premium days</span>
    </div>
  )
}

function GoogleG() {
  return (
    <svg className="h-[16px] w-[16px]" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  )
}
